import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
    Button,
    FormControl,
    FormControlLabel,
    FormLabel,
    makeStyles,
    Menu,
    MenuItem,
    Radio,
    RadioGroup,
} from '@material-ui/core';
import i18n from 'i18n-js';

import {
    isConflict,
    ReservationCancellationAvailabilityDTO,
    ReservationCancellationReason,
    ReservationDTO,
    Reservations,
} from 'api/controllers/reservations';
import { TableMaps } from 'api/controllers/tableMaps';
import { PopupConfirmation } from 'components/shared';
import { theme } from 'config/theme';
import {
    canCancelReservation,
    formatGuestName,
    formatReservationDate,
    getNoShowAvailability,
    getTimePart,
} from 'helpers/reservationHelper';
import { useAppSelector } from 'index';
import globalStore from 'store/globalStore';
import reservationsStore from 'store/reservationsStore';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { ReservableTable } from '../hooks';
import { SignalREvent, signalRService } from 'services/signalR';

const useStyles = makeStyles({
    option: {
        flexDirection: 'column',
        gap: 6,
        minWidth: 260,
        padding: '10px 24px',
        '&.Mui-disabled': { opacity: 1 },
    },
    pill: {
        minWidth: 100,
        textAlign: 'center',
        fontSize: 14,
        borderRadius: 100,
        border: `1px solid ${theme.palette.error.main}`,
        color: theme.palette.error.main,
        '.Mui-disabled &': {
            borderColor: theme.palette.text.disabled,
            color: theme.palette.text.disabled,
        },
        padding: '0 8px',
    },
    hint: { fontSize: 12, color: theme.palette.text.disabled },
});

export default function ReservationActions({
    reservation,
    tables,
    onCancellationDialogChange,
}: {
    reservation: ReservationDTO;
    tables: ReservableTable[];
    onCancellationDialogChange?: (isOpen: boolean) => void;
}) {
    const classes = useStyles();
    const dispatch = useThunkDispatch();
    const timeZone = useAppSelector(
        (state) => state.restaurantDetails.restaurantInformation.timeZone
    );
    const [now, setNow] = useState(() => new Date());
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [isGuestsDepartedOpen, setGuestsDepartedOpen] = useState(false);
    const [isSubmitting, setSubmitting] = useState(false);
    const [cancellation, setCancellation] = useState<ReservationDTO | null>(null);
    const [cancellationTableNames, setCancellationTableNames] = useState('');
    const [reason, setReason] = useState<ReservationCancellationReason>('GuestCancelled');
    const [orderAvailability, setOrderAvailability] =
        useState<ReservationCancellationAvailabilityDTO | null>(null);
    const [isCheckingOrders, setCheckingOrders] = useState(true);
    const [orderCheckFailed, setOrderCheckFailed] = useState(false);
    const checkRequest = useRef(0);
    const isSeated = reservation.status === 'Seated';
    const isCancellationPending = useAppSelector(
        (state) => state.reservations.cancellingId === reservation.id
    );
    const submitting = useRef(false);
    const mounted = useRef(true);
    const [loadedTables, setLoadedTables] = useState<{
        restaurantId: string;
        names: Record<string, string>;
    }>({ restaurantId: reservation.restaurantId, names: {} });
    const assignedNames = reservation.tables.map(
        (id) =>
            tables.find((table) => table.id === id)?.label ||
            orderAvailability?.tables.find((table) => table.tableId === id)?.tableName ||
            (loadedTables.restaurantId === reservation.restaurantId ? loadedTables.names[id] : '')
    );
    const missingIds = reservation.tables.filter((_, index) => !assignedNames[index]);
    const missingKey = JSON.stringify(missingIds);
    const hasTableNames = reservation.tables.length > 0 && missingIds.length === 0;
    const isMenuOpen = !!anchor;
    const availability = getNoShowAvailability(reservation.reservationStartDateTime, timeZone, now);
    const canCancel = isSeated
        ? !reservation.isArchived
        : canCancelReservation(reservation, timeZone, now);
    const sendsEmail = (item: ReservationDTO) => item.channel !== 'WalkIn' && !!item.email?.trim();
    const emailHint = (item: ReservationDTO) =>
        i18n.t(
            sendsEmail(item) ? 'reservations.cancelEmailHint' : 'reservations.cancelNoEmailHint'
        );

    const refreshOrders = useCallback(async () => {
        if (!isSeated) return;
        const request = ++checkRequest.current;
        setCheckingOrders(true);
        try {
            const result = await Reservations.getCancellationAvailability(reservation.id);
            if (mounted.current && request === checkRequest.current) {
                setOrderAvailability(result);
                setOrderCheckFailed(false);
            }
        } catch (error) {
            if (mounted.current && request === checkRequest.current) {
                setOrderCheckFailed(true);
                dispatch(globalStore.actionCreators.showToaster('error', error));
            }
        } finally {
            if (mounted.current && request === checkRequest.current) setCheckingOrders(false);
        }
    }, [
        isSeated,
        reservation.id,
        reservation.version,
        JSON.stringify(reservation.tables),
        dispatch,
    ]);

    useEffect(() => {
        mounted.current = true;
        if (!isSeated) return;
        refreshOrders();
        const refresh = () => {
            refreshOrders();
        };
        const subscriptions = [
            signalRService.subscribe(SignalREvent.OrderAdded, refresh),
            signalRService.subscribe(SignalREvent.OrderChanged, refresh),
            signalRService.subscribe(SignalREvent.ReservationChanged, refresh),
            signalRService.subscribeReconnected(refresh),
        ];
        window.addEventListener('focus', refresh);
        return () => {
            checkRequest.current++;
            subscriptions.forEach((unsubscribe) => unsubscribe());
            window.removeEventListener('focus', refresh);
        };
    }, [isSeated, refreshOrders]);

    const ordersAllowCancellation =
        !isSeated ||
        (!isCheckingOrders && !orderCheckFailed && orderAvailability?.canCancel === true);
    const blockedTableNames =
        orderAvailability?.blockingTables.map((table) => table.tableName).join(' + ') || '';

    useEffect(() => {
        mounted.current = true;
        if (isSeated || !isMenuOpen || missingKey === '[]') {
            return;
        }
        let cancelled = false;
        const ids: string[] = JSON.parse(missingKey);
        Promise.all(ids.map((id) => TableMaps.getMapTable(id)))
            .then((assignedTables) => {
                if (cancelled) {
                    return;
                }
                if (assignedTables.some((table) => !table.name?.trim())) {
                    throw new Error(i18n.t('reservations.tableNamesUnavailable'));
                }
                setLoadedTables((previous) => ({
                    restaurantId: reservation.restaurantId,
                    names: assignedTables.reduce<Record<string, string>>(
                        (names, table) => ({ ...names, [table.id]: table.name }),
                        previous.restaurantId === reservation.restaurantId ? previous.names : {}
                    ),
                }));
            })
            .catch((error) => {
                if (!cancelled) {
                    dispatch(globalStore.actionCreators.showToaster('error', error));
                }
            });
        return () => {
            cancelled = true;
        };
    }, [isSeated, isMenuOpen, missingKey, reservation.restaurantId, dispatch]);

    useEffect(() => {
        const timer = window.setInterval(() => setNow(new Date()), 1000);
        return () => {
            mounted.current = false;
            window.clearInterval(timer);
        };
    }, []);

    async function markNoShow() {
        if (submitting.current || !hasTableNames) {
            return;
        }
        submitting.current = true;
        setSubmitting(true);
        try {
            await dispatch(reservationsStore.actionCreators.markNoShow(reservation.id));
            dispatch(
                globalStore.actionCreators.showToaster(
                    'success',
                    i18n.t('reservations.markedNoShow')
                )
            );
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        } finally {
            submitting.current = false;
            if (mounted.current) {
                setSubmitting(false);
                setDialogOpen(false);
            }
        }
    }

    const tableNames = assignedNames.join(' + ');

    async function confirmGuestsDeparted() {
        if (submitting.current) return;
        submitting.current = true;
        setSubmitting(true);
        try {
            await dispatch(reservationsStore.actionCreators.completeSeated(reservation.id));
            const readyMessage =
                assignedNames.length > 1
                    ? i18n.t('reservations.tablesReady', { tables: tableNames })
                    : i18n.t('reservations.tableReady', { tables: tableNames });
            dispatch(globalStore.actionCreators.showToaster('success', readyMessage));
            setGuestsDepartedOpen(false);
        } catch (error) {
            if (isConflict(error) && error.Code === 'reservationOrdersExist') {
                setGuestsDepartedOpen(false);
                dispatch(
                    globalStore.actionCreators.showToaster(
                        'error',
                        i18n.t('reservations.guestsDepartedOrderConflict', {
                            tables: error.Tables?.map((table) => table.tableName).join(' + ') || tableNames,
                        })
                    )
                );
                refreshOrders();
            } else if (isConflict(error)) {
                setGuestsDepartedOpen(false);
                dispatch(
                    globalStore.actionCreators.showToaster(
                        'error',
                        i18n.t('reservations.changedOnAnotherDevice')
                    )
                );
            } else {
                dispatch(
                    globalStore.actionCreators.showToaster(
                        'error',
                        i18n.t('reservations.couldNotSave')
                    )
                );
            }
        } finally {
            submitting.current = false;
            if (mounted.current) setSubmitting(false);
        }
    }

    async function cancelReservation() {
        if (submitting.current || isCancellationPending || !cancellation) return;
        submitting.current = true;
        setSubmitting(true);
        try {
            const result = await dispatch(
                reservationsStore.actionCreators.cancel(cancellation.id, {
                    expectedVersion: cancellation.version ?? 0,
                    cancellationReason: reason,
                })
            );
            dispatch(
                globalStore.actionCreators.showToaster(
                    result.status === 'cancelled' ? 'success' : 'error',
                    i18n.t(
                        result.status === 'cancelled'
                            ? 'reservations.cancelled'
                            : result.status === 'ordersExist'
                            ? 'reservations.cancelOrderConflict'
                            : 'reservations.changedOnAnotherDevice',
                        {
                            tables:
                                result.status === 'ordersExist'
                                    ? result.tables.map((table) => table.tableName).join(' + ')
                                    : '',
                        }
                    )
                )
            );
            if (result.status === 'cancelled' && result.cancellationEmailFailed) {
                dispatch(
                    globalStore.actionCreators.showToaster(
                        'error',
                        i18n.t('reservations.cancelEmailFailed')
                    )
                );
            }
            if (mounted.current) {
                if (result.status === 'ordersExist') {
                    checkRequest.current++;
                    setCheckingOrders(false);
                    setOrderCheckFailed(false);
                    setOrderAvailability((previous) => ({
                        canCancel: false,
                        tables: previous?.tables || [],
                        blockingTables: result.tables,
                    }));
                }
                setCancellation(null);
                onCancellationDialogChange?.(false);
            }
        } catch (error) {
            dispatch(
                globalStore.actionCreators.showToaster('error', i18n.t('reservations.couldNotSave'))
            );
        } finally {
            submitting.current = false;
            if (mounted.current) setSubmitting(false);
        }
    }

    return (
        <>
            <Button
                variant="contained"
                color="primary"
                fullWidth={isSeated}
                disabled={isSubmitting || isCancellationPending}
                onClick={(event) => {
                    setNow(new Date());
                    setAnchor(event.currentTarget);
                    refreshOrders();
                }}
            >
                {i18n.t('reservations.more')}
            </Button>
            <Menu
                anchorEl={anchor}
                open={!!anchor}
                onClose={() => setAnchor(null)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                {reservation.status === 'Confirmed' && !reservation.isArchived && (
                    <MenuItem
                        className={classes.option}
                        disabled={!availability.isAvailable || isSubmitting || !hasTableNames}
                        onClick={() => {
                            if (!availability.isAvailable || isSubmitting || !hasTableNames) {
                                return;
                            }
                            setAnchor(null);
                            setDialogOpen(true);
                        }}
                    >
                        <span className={classes.pill}>{i18n.t('reservations.noShow')}</span>
                        {!availability.isAvailable && (
                            <span className={classes.hint}>
                                {i18n.t('reservations.noShowAvailableFrom', {
                                    time: availability.availableFrom,
                                })}
                            </span>
                        )}
                        {!hasTableNames && (
                            <span className={classes.hint}>
                                {i18n.t('reservations.tableNamesUnavailable')}
                            </span>
                        )}
                    </MenuItem>
                )}
                {isSeated && (
                    <MenuItem
                        className={classes.option}
                        disabled={
                            isSubmitting ||
                            !hasTableNames ||
                            !ordersAllowCancellation
                        }
                        onClick={() => {
                            setAnchor(null);
                            setGuestsDepartedOpen(true);
                        }}
                    >
                        <span className={classes.pill}>{i18n.t('reservations.guestsDeparted')}</span>
                        <span className={classes.hint}>
                            {isCheckingOrders
                                ? i18n.t('reservations.cancelCheckingOrders')
                                : orderCheckFailed
                                ? i18n.t('reservations.cancelAvailabilityUnavailable')
                                : blockedTableNames
                                ? i18n.t('reservations.cancelOrdersExist', {
                                      tables: blockedTableNames,
                                  })
                                : ''}
                        </span>
                    </MenuItem>
                )}
                {canCancel && (
                    <MenuItem
                        className={classes.option}
                        disabled={
                            isSubmitting ||
                            isCancellationPending ||
                            !hasTableNames ||
                            !ordersAllowCancellation
                        }
                        onClick={() => {
                            setAnchor(null);
                            setReason('GuestCancelled');
                            setCancellationTableNames(tableNames);
                            setCancellation({ ...reservation });
                            onCancellationDialogChange?.(true);
                        }}
                    >
                        <span className={classes.pill}>{i18n.t('reservations.cancel')}</span>
                        <span className={classes.hint}>
                            {isSeated && isCheckingOrders
                                ? i18n.t('reservations.cancelCheckingOrders')
                                : isSeated && orderCheckFailed
                                ? i18n.t('reservations.cancelAvailabilityUnavailable')
                                : isSeated && blockedTableNames
                                ? i18n.t('reservations.cancelOrdersExist', {
                                      tables: blockedTableNames,
                                  })
                                : emailHint(reservation)}
                        </span>
                    </MenuItem>
                )}
            </Menu>
            <PopupConfirmation
                open={!!cancellation}
                isDismissible
                isSubmitting={isSubmitting || isCancellationPending}
                keepOpenOnAction
                preventDismissWhileSubmitting
                showCloseIcon
                actionVariant="contained"
                close={() => {
                    setCancellation(null);
                    onCancellationDialogChange?.(false);
                }}
                title={i18n.t('reservations.cancelTitle')}
                description={
                    cancellation
                        ? i18n.t(
                              cancellation.tables.length > 1
                                  ? 'reservations.cancelDescriptionMany'
                                  : 'reservations.cancelDescriptionOne',
                              {
                                  name: formatGuestName(cancellation),
                                  dateTime: formatReservationDate(
                                      cancellation.reservationStartDateTime,
                                      'EEE dd.MM.yyyy, HH:mm'
                                  ),
                                  guests: cancellation.guestsNumber,
                                  email: sendsEmail(cancellation)
                                      ? i18n.t('reservations.cancelEmailSentence')
                                      : emailHint(cancellation),
                                  tables: cancellationTableNames,
                              }
                          )
                        : ''
                }
                cancelLabel={i18n.t('reservations.cancelBack')}
                activeBtn={i18n.t('reservations.cancelConfirm')}
                action={cancelReservation}
            >
                <FormControl component="fieldset" disabled={isSubmitting || isCancellationPending}>
                    <FormLabel component="legend">{i18n.t('reservations.cancelReason')}</FormLabel>
                    <RadioGroup
                        aria-label={i18n.t('reservations.cancelReason')}
                        value={reason}
                        onChange={(event) => {
                            const value = event.target.value;
                            if (
                                value === 'GuestCancelled' ||
                                value === 'RestaurantClosed' ||
                                value === 'Other'
                            ) {
                                setReason(value);
                            }
                        }}
                    >
                        {(['GuestCancelled', 'RestaurantClosed', 'Other'] as const).map((value) => (
                            <FormControlLabel
                                key={value}
                                value={value}
                                control={<Radio color="primary" />}
                                label={i18n.t(`reservations.cancelReasons.${value}`)}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            </PopupConfirmation>
            <PopupConfirmation
                open={isGuestsDepartedOpen && hasTableNames}
                isDismissible
                showCloseIcon
                isSubmitting={isSubmitting}
                keepOpenOnAction
                actionVariant="contained"
                close={() => setGuestsDepartedOpen(false)}
                title={i18n.t('reservations.guestsDepartedTitle')}
                description={i18n.t('reservations.guestsDepartedDescription')}
                cancelLabel={i18n.t('reservations.guestsDepartedBack')}
                activeBtn={i18n.t('reservations.guestsDepartedConfirm')}
                action={confirmGuestsDeparted}
            />
            <PopupConfirmation
                open={isDialogOpen && hasTableNames}
                isDismissible
                isSubmitting={isSubmitting}
                keepOpenOnAction
                actionVariant="contained"
                close={() => setDialogOpen(false)}
                title={i18n.t('reservations.noShowTitle')}
                description={i18n.t(
                    reservation.tables.length > 1
                        ? 'reservations.noShowDescriptionMany'
                        : 'reservations.noShowDescriptionOne',
                    {
                        name: formatGuestName(reservation),
                        time: getTimePart(reservation.reservationStartDateTime),
                        guests: reservation.guestsNumber,
                        tables: tableNames,
                    }
                )}
                activeBtn={i18n.t('reservations.markNoShow')}
                action={markNoShow}
            />
        </>
    );
}
